import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import MicIcon from "@mui/icons-material/Mic";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";

import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogContent,
  Tabs,
  Tab,
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
  FormControlLabel,
  DialogActions,
  Divider,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import Grow from "@mui/material/Grow";
import { MdOutlineFileDownload } from "react-icons/md";
import DownloadIcon from "@mui/icons-material/Download";
import Chip from "@mui/material/Chip"; // <-- ADD THIS LINE
import {
  useGetMockTestByIdQuery,
  useLazyGetMockTestDataQuery,
  useCreateTestAttemptMutation,
  useLazyGetPracticeTestDataQuery,
  useGetMockTestPdfQuery,
  TEST_TYPES,
  TEST_ROUTES,
  TEST_BACK_ROUTES,
} from "../mockTestSlice";
import { clearPracticeTimer } from "../utils/practiceTimer";
import {
  InstructionWrapper,
  useInstructions,
} from "../../../components/Instructions";
export default function SingleMockTest({ testType = TEST_TYPES.MOCK_TEST }) {
  const { testId } = useParams();
  const navigate = useNavigate();

  // Get the base route based on test type
  const getBaseRoute = () => {
    return TEST_ROUTES[testType] || "/mock-test";
  };
  const baseRoute = getBaseRoute();
  const backRoute = TEST_BACK_ROUTES[testType] || "/app/mocktests";

  // --- Instruction system integration ---
  const {
    instructionState,
    showInstructionModal,
    hideInstructionModal,
    showConfirmationDialog,
    hideConfirmationDialog,
    showUnattemptedDialog,
    hideUnattemptedDialog,
    showTimeExpiryDialog,
    hideTimeExpiryDialog,
  } = useInstructions();

  // Tutorial completion state
  const [tutorialCompleted, setTutorialCompleted] = useState(false);

  const [simulationStep, setSimulationStep] = useState({
    module: null, // Will be set dynamically based on available modules: listening -> reading -> writing -> speaking
    listeningPartIndex: 0,
    readingPartIndex: 0,
  });

  // Fetch test metadata
  const { data: mockTest, isLoading, error } = useGetMockTestByIdQuery(testId);
  console.log("🚀 ~ SingleMockTest ~ mockTest:", mockTest);

  // Helper to determine which modules are available in the package
  const availableModules = {
    listening: !!(
      mockTest?.ieltsTest?.listeningId || mockTest?.oetTest?.listeningId
    ),
    reading: !!(mockTest?.ieltsTest?.readingId || mockTest?.oetTest?.readingId),
    writing: !!(mockTest?.ieltsTest?.writingId || mockTest?.oetTest?.writingId),
    speaking: !!(
      mockTest?.ieltsTest?.speakingId || mockTest?.oetTest?.speakingId
    ),
  };

  const {
    data: pdfApiData,
    isLoading: loadingPdf,
    error: pdfError,
  } = useGetMockTestPdfQuery(testId, { skip: !testId });

  console.log("PDF API RAW:", pdfApiData);

  // Modal state
  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState(0);

  // Error notification state
  const [errorNotification, setErrorNotification] = useState({
    open: false,
    message: "",
    severity: "error", // "error", "warning", "info", "success"
  });

  // Section selection state
  const [modeSelection, setModeSelection] = useState({
    listening: false,
    reading: false,
    writing: false,
  });

  // Writing tasks state
  const [writingTasks, setWritingTasks] = useState([]);
  const [selectedWritingTasks, setSelectedWritingTasks] = useState({});
  const [loadingWriting, setLoadingWriting] = useState(false);

  // Timer state for practice mode - separate timers for each section
  const [readingMinutes, setReadingMinutes] = useState(45); // Default to 45 minutes for reading
  const [writingMinutes, setWritingMinutes] = useState(45); // Default to 45 minutes for writing
  const [speakingMinutes, setSpeakingMinutes] = useState(20); // Default to 20 minutes for speaking
  // Listening will use audio-based timing, no timer minutes needed

  // Listening/Reading parts state
  const [listeningParts, setListeningParts] = useState([]);
  const [readingParts, setReadingParts] = useState([]);
  const initialReadingPartsRef = useRef(null);

  // Which parts are checked
  const [selectedListeningParts, setSelectedListeningParts] = useState({});
  const [selectedReadingParts, setSelectedReadingParts] = useState({});
  const [selectedReadingQuestions, setSelectedReadingQuestions] = useState({});
  const [listeningNonce, setListeningNonce] = useState(Date.now());

  // Audio completion tracking - mark audio as complete when user stops it
  const [audioCompleted, setAudioCompleted] = useState({});

  // Function to mark audio as complete when user stops playing
  const markAudioComplete = (partId) => {
    setAudioCompleted((prev) => ({
      ...prev,
      [partId]: true,
    }));
  };

  // Function to check if audio is completed (either fully played or manually stopped)
  const isAudioComplete = (partId) => {
    return audioCompleted[partId] || false;
  };
  // Only listening
  useEffect(() => {
    if (!mockTest?.ieltsTest && !mockTest?.oetTest) return;
    if (
      modeSelection.listening &&
      (mockTest.ieltsTest?.listeningId || mockTest.oetTest?.listeningId)
    ) {
      triggerGetListening({
        listeningTestId:
          mockTest.ieltsTest?.listeningId || mockTest.oetTest?.listeningId,
        testType: mockTest.discipline || "ielts",
        nonce: listeningNonce, // force cache bust
      });
    }
  }, [modeSelection.listening, mockTest, listeningNonce]);
  useEffect(() => {
    if (!mockTest?.ieltsTest && !mockTest?.oetTest) return;
    if (
      modeSelection.reading &&
      (mockTest.ieltsTest?.readingId || mockTest.oetTest?.readingId)
    ) {
      triggerGetReading({
        readingTestId:
          mockTest.ieltsTest?.readingId || mockTest.oetTest?.readingId,
        testType: mockTest.discipline || "ielts",
      });
    }
  }, [modeSelection.reading, mockTest]);

  useEffect(() => {
    if (!mockTest?.ieltsTest && !mockTest?.oetTest) return;
    if (
      modeSelection.writing &&
      (mockTest.ieltsTest?.writingId || mockTest.oetTest?.writingId)
    ) {
      // For writing, we need to create a temporary test attempt to get the tasks
      // since writing tasks come from the practice test data API
      fetchWritingTasks();
    }
  }, [modeSelection.writing, mockTest]);

  // Function to fetch writing tasks using mock test data API (like listening/reading)
  const fetchWritingTasks = async () => {
    setLoadingWriting(true);
    try {
      // Use the same API that listening/reading use to get writing tasks
      const writingResponse = await triggerGetWriting({
        writingTestId:
          mockTest.ieltsTest?.writingId || mockTest.oetTest?.writingId,
        testType: mockTest.discipline || "ielts",
      }).unwrap();

      console.log("Writing tasks mock test data response:", writingResponse);
      console.log(
        "Full response structure:",
        JSON.stringify(writingResponse, null, 2)
      );

      // The API response has writing tasks in writingTask array (not writingTasks)
      const writingTasksArray = writingResponse?.data?.writingTask;

      console.log("Writing tasks array:", writingTasksArray);

      // Process the response to extract writing tasks
      if (writingTasksArray && Array.isArray(writingTasksArray)) {
        console.log(
          "Setting writing tasks from mock test data API:",
          writingTasksArray
        );
        setWritingTasks(writingTasksArray);
      } else {
        console.log("No writing tasks found in response. Checked paths:");
        console.log(
          "- writingResponse?.data?.writingTask:",
          writingResponse?.data?.writingTask
        );
        setWritingTasks([]);
      }
    } catch (error) {
      console.error("Failed to fetch writing tasks:", error);
      setWritingTasks([]);
    } finally {
      setLoadingWriting(false);
    }
  };

  const [createTestAttempt] = useCreateTestAttemptMutation();
  const SIM_SECTIONS = [
    { key: "listening", label: "Listening", idField: "listeningId" },
    { key: "reading", label: "Reading", idField: "readingId" },
    { key: "writing", label: "Writing", idField: "writingId" },
    // Speaking is not included in simulation mode
  ];

  // Get available simulation sections based on package
  const availableSimSections = SIM_SECTIONS.filter(
    (section) =>
      mockTest?.ieltsTest?.[section.idField] ||
      mockTest?.oetTest?.[section.idField]
  );

  // Find the first available section index
  const getFirstAvailableSectionIndex = () => {
    return SIM_SECTIONS.findIndex(
      (section) =>
        mockTest?.ieltsTest?.[section.idField] ||
        mockTest?.oetTest?.[section.idField]
    );
  };

  const [currentSimSection, setCurrentSimSection] = useState(null); // Will be set dynamically based on available modules
  const [simulationData, setSimulationData] = useState(null);
  const [simulationLoading, setSimulationLoading] = useState(false);

  // Initialize currentSimSection to the first available section when mockTest loads
  useEffect(() => {
    if (mockTest && currentSimSection === null) {
      const firstAvailableIndex = getFirstAvailableSectionIndex();
      if (firstAvailableIndex >= 0) {
        setCurrentSimSection(firstAvailableIndex);
        // Also set the initial simulationStep module
        const firstSection = SIM_SECTIONS[firstAvailableIndex];
        setSimulationStep((prev) => ({
          ...prev,
          module: firstSection?.key || null,
        }));
      }
    }
  }, [mockTest]);

  const sectionPaths = ["listening", "reading", "writing"];

  const handleSimSection = async () => {
    // If this is the start button (first available section) and tutorial not completed, show tutorial first
    const firstAvailableIndex = getFirstAvailableSectionIndex();
    if (currentSimSection === firstAvailableIndex && !tutorialCompleted) {
      showInstructionModal("tutorial", null, "simulation");
      return;
    }

    // Proceed with actual test start
    await startSimulationTest();
  };

  const startSimulationTest = async () => {
    setSimulationLoading(true);
    setSimulationData(null);

    try {
      // Prepare ieltsTest object with the current section ID
      const testData = {};
      const currentSection = SIM_SECTIONS[currentSimSection];

      // Get the appropriate ID field from mockTest.ieltsTest or oetTest
      if (mockTest?.ieltsTest && mockTest.ieltsTest[currentSection.idField]) {
        testData[currentSection.key + "Id"] =
          mockTest.ieltsTest[currentSection.idField];
      } else if (
        mockTest?.oetTest &&
        mockTest.oetTest[currentSection.idField]
      ) {
        testData[currentSection.key + "Id"] =
          mockTest.oetTest[currentSection.idField];
      }

      console.log("Simulation test request with:", {
        mockTestId: testId,
        isPracticeMode: false,
        ieltsTest: Object.keys(testData).length > 0 ? testData : undefined,
      });

      // Clear any existing practice timer data before starting new test
      clearPracticeTimer();

      // Create test attempt for simulation mode
      const createAttemptResponse = await createTestAttempt({
        mockTestId: testId,
        isPracticeMode: false,
        ieltsTest: Object.keys(testData).length > 0 ? testData : undefined,
      }).unwrap();

      console.log("Test attempt created:", createAttemptResponse);
      const studentAttemptId = createAttemptResponse.studentAttemptId;

      if (!studentAttemptId) {
        throw new Error("No studentAttemptId received from create attempt API");
      }

      // Extract ids for step navigation - handle both OET and IELTS structures
      const responseOetTest = createAttemptResponse.oetTest || {};
      const responseIeltsTest = createAttemptResponse.ieltsTest || {};

      // Use IELTS data
      // Use listeningSectionIds for IELTS
      const listeningPartIds =
        responseIeltsTest.listeningSectionIds ||
        responseOetTest.listeningPartIds ||
        [];
      const listeningId =
        responseIeltsTest.listeningId || responseOetTest.listeningId;
      // Use readingSectionIds for IELTS reading
      const readingSectionIds =
        responseIeltsTest.readingSectionIds ||
        responseOetTest.readingPartIds ||
        [];
      const readingId =
        responseIeltsTest.readingId || responseOetTest.readingId;
      // NEW: Use writingTaskIds for IELTS writing
      const writingTaskIds =
        responseIeltsTest.writingTaskIds ||
        responseOetTest.writingTaskIds ||
        [];
      const writingId =
        responseIeltsTest.writingId || responseOetTest.writingId;
      const speakingId =
        responseIeltsTest.speakingId || responseOetTest.speakingId;

      // Only step-by-step for simulation
      let nextState = {
        studentAttemptId,
        mockTest: {
          ...mockTest,
          oetTest: createAttemptResponse.oetTest, // Use oetTest from createTestAttempt response
          ieltsTest: createAttemptResponse.ieltsTest, // Include IELTS test data
        },
        testId,
        isPracticeMode: false,
        listeningTestId: listeningId,
        readingTestId: readingId,
        writingTestId: writingId,
        speakingTestId: speakingId,
        // Store the full arrays for later navigation
        listeningSectionIds: listeningPartIds,
        readingSectionIds: readingSectionIds,
        writingTaskIds: writingTaskIds,
      };

      // Dynamically set simulationStep based on which module we're starting with
      const startingModule = SIM_SECTIONS[currentSimSection].key;

      if (startingModule === "listening") {
        if (listeningPartIds.length > 0) {
          nextState.simulationStep = {
            module: "listening",
            listeningPartIndex: 0,
            totalListeningParts: listeningPartIds.length,
            currentListeningPartId: listeningPartIds[0],
            listeningSectionIds: listeningPartIds,
            readingSectionIds: readingSectionIds,
            totalReadingParts: readingSectionIds.length,
            writingTaskIds: writingTaskIds,
          };
        } else {
          nextState.simulationStep = {
            module: "listening",
            listeningPartIndex: 0,
            totalListeningParts: 1,
            currentListeningPartId: listeningId,
            listeningSectionIds: [listeningId],
            readingSectionIds: readingSectionIds,
            totalReadingParts: readingSectionIds.length,
            writingTaskIds: writingTaskIds,
          };
        }
      } else if (startingModule === "reading") {
        nextState.simulationStep = {
          module: "reading",
          readingPartIndex: 0,
          totalReadingParts: readingSectionIds.length || 1,
          currentReadingSectionId: readingSectionIds[0] || readingId,
          readingSectionIds:
            readingSectionIds.length > 0 ? readingSectionIds : [readingId],
          writingTaskIds: writingTaskIds,
          listeningSectionIds: listeningPartIds, // Keep for reference
        };
      } else if (startingModule === "writing") {
        nextState.simulationStep = {
          module: "writing",
          writingTaskIndex: 0,
          totalWritingTasks: writingTaskIds.length || 1,
          currentWritingTaskId: writingTaskIds[0] || writingId,
          writingTaskIds:
            writingTaskIds.length > 0 ? writingTaskIds : [writingId],
          readingSectionIds: readingSectionIds,
          listeningSectionIds: listeningPartIds,
        };
      }
      // Speaking is removed from simulation mode

      // Add testType and baseRoute to state for downstream navigation
      nextState.testType = testType;
      nextState.baseRoute = baseRoute;
      nextState.backRoute = backRoute;

      // Always go to first section selected in Simulation (usually Listening)
      const sectionPath = sectionPaths[currentSimSection];

      navigate(`${baseRoute}/${testId}/${sectionPath}`, {
        state: nextState,
      });
    } catch (error) {
      console.error("Failed to create test attempt:", error);
      alert("Failed to start simulation test. Please try again.");
    } finally {
      setSimulationLoading(false);
    }
  };

  const [selectOpen, setSelectOpen] = useState(false);
  const selectRef = useRef(null);

  // clear related selections when unchecking (you likely already have these setters)

  const handleChipDelete = (e, key) => {
    // stop MUI from toggling the Select open
    e.stopPropagation();
    e.preventDefault();

    // explicitly close and blur the select element
    setSelectOpen(false);
    if (selectRef.current && typeof selectRef.current.blur === "function") {
      selectRef.current.blur();
    }

    // update your selection state
    setModeSelection((prev) => ({
      ...prev,
      [key]: false,
    }));

    // clear dependent selections
    if (key === "listening" && setSelectedListeningParts) {
      setSelectedListeningParts({});
    }
    if (key === "reading") {
      if (setSelectedReadingParts) setSelectedReadingParts({});
      if (setSelectedReadingQuestions) setSelectedReadingQuestions({});
    }
  };
  // Handle tutorial completion and proceed to test
  const handleTutorialComplete = () => {
    setTutorialCompleted(true);
    hideInstructionModal();
    // Now start the actual test
    startSimulationTest();
  };

  // Handle cancel and navigate back to mock tests
  const handleCancel = () => {
    setOpen(false);
    navigate(backRoute);
  };

  // Fetch section-parts from backend
  const [
    triggerGetListening,
    {
      data: listeningData,
      isLoading: loadingListening,
      refetch: refetchListening,
    },
  ] = useLazyGetMockTestDataQuery();
  const [triggerGetReading, { data: readingData, isLoading: loadingReading }] =
    useLazyGetMockTestDataQuery();
  const [triggerGetWriting, { data: writingData }] =
    useLazyGetMockTestDataQuery();
  const [triggerGetPracticeData] = useLazyGetPracticeTestDataQuery();

  // Add persistent state for reading questions to prevent them from disappearing
  const [persistentReadingQuestions, setPersistentReadingQuestions] = useState(
    []
  );

  // Debug: Track readingData changes
  useEffect(() => {
    console.log("=== READING DATA CHANGED ===");
    console.log("readingData:", readingData);
    console.log(
      "readingData questions:",
      readingData?.data?.readingQuestions?.length || 0
    );
    console.log("Current modeSelection:", modeSelection);
    console.log("============================");
  }, [readingData, modeSelection]);

  // We'll fetch practice data step by step in the test page, not here

  // When a section is checked, request its parts from backend

  // When partData returns, update checkbox lists
  useEffect(() => {
    // Listening API response - handle both old listeningParts and new listeningSections
    const apiListening = listeningData?.data || {};
    if (
      apiListening.listeningSections &&
      Array.isArray(apiListening.listeningSections)
    ) {
      // Convert listeningSections to the expected format for checkboxes
      const sections = apiListening.listeningSections.map((section, index) => ({
        _id: section._id,
        part: `Section ${index + 1}`, // or use section.sectionName if available
        sectionName: section.sectionName || `Section ${index + 1}`,
      }));
      setListeningParts(sections);
    } else if (apiListening.listeningParts) {
      // Fallback to old structure
      setListeningParts(apiListening.listeningParts);
    } else {
      setListeningParts([]);
    }
  }, [listeningData]);

  useEffect(() => {
    // Reading API response - only process when we have actual reading data
    if (!readingData) return;

    console.log("Processing reading API response:", readingData);
    console.log("Current reading mode selection:", modeSelection.reading);
    const apiReading = readingData?.data || {};

    // Handle both old readingParts and new readingSections
    if (
      Array.isArray(apiReading.readingSections) &&
      apiReading.readingSections.length
    ) {
      console.log(
        "Setting reading sections from API:",
        apiReading.readingSections
      );
      // Convert readingSections to the expected format for checkboxes
      const sections = apiReading.readingSections.map((section) => ({
        _id: section._id,
        part: section.sectionName || "Section",
        sectionName: section.sectionName,
        sectionHeading: section.sectionHeading,
      }));
      console.log("Converted sections for checkboxes:", sections);
      setReadingParts(sections);
      if (!initialReadingPartsRef.current) {
        initialReadingPartsRef.current = sections;
      }
    } else if (
      Array.isArray(apiReading.readingParts) &&
      apiReading.readingParts.length
    ) {
      console.log("Setting reading parts from API:", apiReading.readingParts);
      setReadingParts(apiReading.readingParts);
      if (!initialReadingPartsRef.current) {
        initialReadingPartsRef.current = apiReading.readingParts;
      }
    }

    // Persist reading questions when we get them from API
    if (
      Array.isArray(apiReading.readingQuestions) &&
      apiReading.readingQuestions.length > 0
    ) {
      console.log(
        "Reading questions available from API:",
        apiReading.readingQuestions.length
      );
      console.log("Updating persistent reading questions");
      setPersistentReadingQuestions(apiReading.readingQuestions);
    }
    // Reading questions are automatically available via readingData?.data?.readingQuestions
    // No need to set them as state since they're derived from the API response
    // Only clear reading questions if we're sure this response was for reading
    // and there are actually no questions in the response
    // Don't clear if reading is not selected or if this might be data for another section
  }, [readingData, modeSelection.reading]); // Add back modeSelection.reading but keep safe logic

  // Handlers for section checkboxes
  const handleSectionToggle = (section) => (e) => {
    const checked = e.target.checked;
    setModeSelection((prev) => ({ ...prev, [section]: checked }));

    if (!checked) {
      if (section === "listening") {
        setListeningParts([]);
        setSelectedListeningParts({});
      }
      if (section === "reading") {
        setSelectedReadingParts({});
        setSelectedReadingQuestions({});
        setPersistentReadingQuestions([]);
      }
      if (section === "writing") {
        setWritingTasks([]);
        setSelectedWritingTasks({});
      }
    } else if (checked) {
      if (section === "listening") {
        setListeningNonce(Date.now());
      }
    }
  };

  // Handler for part checkboxes
  const handlePartToggle = (type, id) => (e) => {
    console.log("handlePartToggle called with:", {
      type,
      id,
      checked: e.target.checked,
    });
    if (type === "listening") {
      setSelectedListeningParts((prev) => ({
        ...prev,
        [id]: e.target.checked,
      }));
    }
    if (type === "reading") {
      setSelectedReadingParts((prev) => {
        const newState = {
          ...prev,
          [id]: e.target.checked,
        };
        console.log("New selectedReadingParts state:", newState);
        return newState;
      });

      // Auto-select/deselect all questions for this reading section
      if (e.target.checked) {
        console.log("Reading section checked:", id);
        // Find the section from the reading parts
        const selectedPart = stableReadingParts.find((p) => p._id === id);
        console.log("Found selected section:", selectedPart);
        if (selectedPart && questionsByPart[selectedPart.part]) {
          console.log(
            "Questions for this section:",
            questionsByPart[selectedPart.part]
          );
          const questionsToSelect = {};
          questionsByPart[selectedPart.part].forEach((q) => {
            questionsToSelect[q._id] = true;
          });
          setSelectedReadingQuestions((prev) => ({
            ...prev,
            ...questionsToSelect,
          }));
        }
      } else {
        // Deselect all questions for this reading section
        console.log("Reading section unchecked:", id);
        const selectedPart = stableReadingParts.find((p) => p._id === id);
        console.log("Found deselected section:", selectedPart);
        if (selectedPart && questionsByPart[selectedPart.part]) {
          const questionsToDeselect = {};
          questionsByPart[selectedPart.part].forEach((q) => {
            questionsToDeselect[q._id] = false;
          });
          setSelectedReadingQuestions((prev) => ({
            ...prev,
            ...questionsToDeselect,
          }));
        }
      }
    }
    if (type === "writing") {
      setSelectedWritingTasks((prev) => ({
        ...prev,
        [id]: e.target.checked,
      }));
    }
    if (type === "readingQuestion") {
      setSelectedReadingQuestions((prev) => ({
        ...prev,
        [id]: e.target.checked,
      }));
    }
  };

  // Send selected part ids to backend whenever selection changes
  useEffect(() => {
    // This API call is for fetching questions metadata (not practice mode test data)
    console.log(
      "Part selection changed, checking if we need to fetch questions metadata"
    );

    const readingSectionId = Object.keys(selectedReadingParts).filter(
      (id) => selectedReadingParts[id]
    );
    const listeningSectionId = Object.keys(selectedListeningParts).filter(
      (id) => selectedListeningParts[id]
    );

    console.log("Current selectedReadingParts:", selectedReadingParts);
    console.log("Filtered readingSectionId:", readingSectionId);
    console.log("Available reading sections:", stableReadingParts);

    // Call the API for getting questions metadata when reading sections are selected
    // This is different from practice mode data fetching - this is just for showing question checkboxes
    if (readingSectionId.length > 0 && modeSelection.reading) {
      console.log(
        "Fetching reading questions metadata for sections:",
        readingSectionId
      );
      const params = {
        readingSectionId: readingSectionId,
      };

      // Add readingId from mockTest
      if (mockTest?.ieltsTest?.readingId || mockTest?.oetTest?.readingId) {
        params.readingTestId =
          mockTest.ieltsTest?.readingId || mockTest.oetTest?.readingId;
      }

      // Add testType
      params.testType = mockTest.discipline || "ielts";

      triggerGetReading(params);
    }

    // Similarly for listening sections if needed
    if (listeningSectionId.length > 0 && modeSelection.listening) {
      console.log(
        "Fetching listening sections metadata for sections:",
        listeningSectionId
      );
      const params = {
        listeningSectionId: listeningSectionId,
      };

      if (mockTest?.ieltsTest?.listeningId || mockTest?.oetTest?.listeningId) {
        params.listeningTestId =
          mockTest.ieltsTest?.listeningId || mockTest.oetTest?.listeningId;
      }

      // Note: We might need a separate API call for listening parts metadata
      // For now, we're not calling it since listening doesn't need individual question selection
    }
  }, [
    selectedReadingParts,
    selectedListeningParts,
    triggerGetReading,
    mockTest?.ieltsTest?.readingId,
    mockTest?.oetTest?.readingId,
    mockTest?.ieltsTest?.listeningId,
    mockTest?.oetTest?.listeningId,
    modeSelection.listening,
    modeSelection.reading,
  ]);

  const selectedLabels = [];
  if (modeSelection.listening) selectedLabels.push("Listening");
  if (modeSelection.reading) selectedLabels.push("Reading");
  if (modeSelection.writing) selectedLabels.push("Writing");

  const handleFinishSelection = async () => {
    // Check if at least one section is selected
    const hasSelection = Object.values(modeSelection).some(Boolean);
    if (!hasSelection) {
      setErrorNotification({
        open: true,
        message: "Please select at least one section to practice.",
        severity: "warning",
      });
      return;
    }

    // Check if selected sections have required parts selected
    if (modeSelection.listening && listeningParts.length > 0) {
      const hasListeningParts = Object.values(selectedListeningParts).some(
        Boolean
      );
      if (!hasListeningParts) {
        setErrorNotification({
          open: true,
          message: "Please select at least one listening part.",
          severity: "warning",
        });
        return;
      }
    }

    if (modeSelection.reading && stableReadingParts.length > 0) {
      const hasReadingParts = Object.values(selectedReadingParts).some(Boolean);
      if (!hasReadingParts) {
        setErrorNotification({
          open: true,
          message: "Please select at least one reading part.",
          severity: "warning",
        });
        return;
      }

      // Check if selected reading parts have questions selected
      const hasReadingQuestions = Object.values(selectedReadingQuestions).some(
        Boolean
      );
      if (!hasReadingQuestions) {
        setErrorNotification({
          open: true,
          message:
            "Please select at least one question from the selected reading parts.",
          severity: "warning",
        });
        return;
      }
    }

    if (modeSelection.writing && writingTasks.length > 0) {
      const hasWritingTasks = Object.values(selectedWritingTasks).some(Boolean);
      if (!hasWritingTasks) {
        setErrorNotification({
          open: true,
          message: "Please select at least one writing task.",
          severity: "warning",
        });
        return;
      }
    }

    try {
      // Prepare ieltsTest object with selected IDs
      const ieltsTest = {};

      // Add test IDs and selected parts for each section
      if (
        modeSelection.listening &&
        (mockTest?.ieltsTest?.listeningId || mockTest?.oetTest?.listeningId)
      ) {
        // Add the main test ID
        ieltsTest.listeningId =
          mockTest.ieltsTest?.listeningId || mockTest.oetTest?.listeningId;

        // Add selected sections as an array (IELTS structure)
        let selectedSectionIds = Object.keys(selectedListeningParts).filter(
          (id) => selectedListeningParts[id]
        );

        // Sort sections by their index in listeningParts to maintain natural order
        const sectionIndexMap = {};
        listeningParts.forEach((section, index) => {
          sectionIndexMap[section._id] = index;
        });
        selectedSectionIds.sort((a, b) => {
          return (sectionIndexMap[a] || 0) - (sectionIndexMap[b] || 0);
        });

        if (selectedSectionIds.length > 0) {
          ieltsTest.listeningSectionIds = selectedSectionIds;
        }
      }

      if (
        modeSelection.reading &&
        (mockTest?.ieltsTest?.readingId || mockTest?.oetTest?.readingId)
      ) {
        // Add the main test ID
        ieltsTest.readingId =
          mockTest.ieltsTest?.readingId || mockTest.oetTest?.readingId;

        // Add selected sections as an array (IELTS structure)
        let selectedSectionIds = Object.keys(selectedReadingParts).filter(
          (id) => selectedReadingParts[id]
        );

        // Sort sections by their index in readingParts/stableReadingParts to maintain natural order
        const stableReadingPartsForSort =
          readingParts.length > 0
            ? readingParts
            : initialReadingPartsRef.current || [];
        const sectionIndexMap = {};
        stableReadingPartsForSort.forEach((section, index) => {
          sectionIndexMap[section._id] = index;
        });
        selectedSectionIds.sort((a, b) => {
          return (sectionIndexMap[a] || 0) - (sectionIndexMap[b] || 0);
        });

        if (selectedSectionIds.length > 0) {
          ieltsTest.readingSectionIds = selectedSectionIds;
        }

        // Add selected questions as an array
        const selectedQuestionIds = Object.keys(
          selectedReadingQuestions
        ).filter((id) => selectedReadingQuestions[id]);
        if (selectedQuestionIds.length > 0) {
          ieltsTest.readingQuestionIds = selectedQuestionIds;
        }
      }

      if (
        modeSelection.writing &&
        (mockTest?.ieltsTest?.writingId || mockTest?.oetTest?.writingId)
      ) {
        // Add the writing test ID
        ieltsTest.writingId =
          mockTest.ieltsTest?.writingId || mockTest.oetTest?.writingId;

        // Add selected writing tasks if any are selected
        const selectedTaskIds = Object.keys(selectedWritingTasks).filter(
          (id) => selectedWritingTasks[id]
        );
        if (selectedTaskIds.length > 0) {
          ieltsTest.writingTaskIds = selectedTaskIds;
        }
      }

      console.log("Sending practice test request with:", {
        mockTestId: testId,
        isPracticeMode: true,
        ieltsTest,
        readingMinutes,
        writingMinutes,
        speakingMinutes,
      });

      // Clear any existing practice timer data before starting new test
      clearPracticeTimer();

      // Create test attempt for practice mode with all selected parts and questions
      const createAttemptResponse = await createTestAttempt({
        mockTestId: testId,
        isPracticeMode: true,
        ieltsTest: Object.keys(ieltsTest).length > 0 ? ieltsTest : undefined,
        readingMinutes,
        writingMinutes,
        speakingMinutes,
      }).unwrap();

      console.log("Practice test attempt created:", createAttemptResponse);
      const studentAttemptId = createAttemptResponse.studentAttemptId;

      if (!studentAttemptId) {
        throw new Error("No studentAttemptId received from create attempt API");
      }

      // Get the test data from the response (OET or IELTS)
      const responseOetTest = createAttemptResponse.oetTest;
      const responseIeltsTest = createAttemptResponse.ieltsTest;

      // Fetch practice test data for the first section
      let initialPracticeData = null;
      try {
        console.log("Fetching initial practice test data with:", {
          studentAttemptId,
          oetTest: responseOetTest,
          ieltsTest: responseIeltsTest,
        });

        // Determine which section to fetch first
        const firstSection = modeSelection.listening
          ? "listening"
          : modeSelection.reading
          ? "reading"
          : modeSelection.writing
          ? "writing"
          : null;

        if (firstSection) {
          // For practice mode, only get data for the first listening part if multiple parts selected
          let apiParams = {
            studentAttemptId,
            currentSection: firstSection,
            listeningId:
              responseIeltsTest?.listeningId || responseOetTest?.listeningId,
            readingId:
              responseIeltsTest?.readingId || responseOetTest?.readingId,
            writingId:
              responseIeltsTest?.writingId || responseOetTest?.writingId,
            listeningPartIds:
              responseIeltsTest?.listeningSectionIds ||
              responseOetTest?.listeningPartIds,
            readingSectionIds:
              responseIeltsTest?.readingSectionIds ||
              responseOetTest?.readingPartIds,
            readingQuestionIds:
              responseIeltsTest?.readingQuestionIds ||
              responseOetTest?.readingQuestionIds,
            writingTaskIds:
              responseIeltsTest?.writingTaskIds ||
              responseOetTest?.writingTaskIds,
          };

          // If listening section and multiple parts selected, only fetch first part
          if (
            firstSection === "listening" &&
            apiParams.listeningPartIds?.length > 1
          ) {
            apiParams.listeningPartIds = [apiParams.listeningPartIds[0]];
            console.log(
              "Fetching only first listening part for step-by-step:",
              apiParams.listeningPartIds
            );
          }

          // If reading section and multiple sections selected, only fetch first section
          if (
            firstSection === "reading" &&
            apiParams.readingSectionIds?.length > 0
          ) {
            // For reading, use singular readingSectionId parameter
            apiParams.readingSectionId = apiParams.readingSectionIds[0];
            delete apiParams.readingSectionIds; // Remove array parameter
            console.log(
              "Fetching only first reading section for step-by-step:",
              apiParams.readingSectionId
            );
          }

          // Call practice test data API with the first selected section
          const practiceResponse = await triggerGetPracticeData(
            apiParams
          ).unwrap();

          console.log("Initial practice test data response:", practiceResponse);
          initialPracticeData = practiceResponse;
        }
      } catch (error) {
        console.error("Error fetching practice test data:", error);
        // Continue with navigation even if this fails
      } // Navigate to the first selected section
      let firstSelectedSection = null;
      if (modeSelection.listening) firstSelectedSection = "listening";
      else if (modeSelection.reading) firstSelectedSection = "reading";
      else if (modeSelection.writing) firstSelectedSection = "writing";

      if (firstSelectedSection) {
        // Define the order of sections for step navigation
        const sectionOrder = [];
        if (modeSelection.listening) sectionOrder.push("listening");
        if (modeSelection.reading) sectionOrder.push("reading");
        if (modeSelection.writing) sectionOrder.push("writing");

        navigate(`${baseRoute}/${testId}/${firstSelectedSection}`, {
          state: {
            studentAttemptId,
            mockTest: {
              ...mockTest,
              oetTest: createAttemptResponse.oetTest, // Use oetTest from createTestAttempt response
              ieltsTest: createAttemptResponse.ieltsTest, // Include IELTS test data
              testStartTime: createAttemptResponse.testStartTime, // Add API timer start
              testEndDate: createAttemptResponse.testEndDate, // Add API timer end
            },
            testId,
            isPracticeMode: true,
            selectedSections: modeSelection,
            readingMinutes, // Add separate timer values to state
            writingMinutes,
            speakingMinutes,
            // Use test IDs from createTestAttempt response (IELTS takes precedence)
            listeningTestId:
              createAttemptResponse.ieltsTest?.listeningId ||
              createAttemptResponse.oetTest?.listeningId,
            readingTestId:
              createAttemptResponse.ieltsTest?.readingId ||
              createAttemptResponse.oetTest?.readingId,
            writingTestId:
              createAttemptResponse.ieltsTest?.writingId ||
              createAttemptResponse.oetTest?.writingId,
            // Include selected parts data
            selectedListeningParts,
            selectedReadingParts,
            selectedReadingQuestions,
            selectedWritingTasks,
            // Step-by-step navigation support
            sectionOrder,
            currentSectionIndex: 0,
            // IDs needed for step-by-step API calls (IELTS takes precedence)
            listeningPartIds:
              createAttemptResponse.ieltsTest?.listeningSectionIds ||
              createAttemptResponse.oetTest?.listeningPartIds,
            readingSectionIds:
              createAttemptResponse.ieltsTest?.readingSectionIds ||
              createAttemptResponse.oetTest?.readingPartIds,
            readingQuestionIds:
              createAttemptResponse.ieltsTest?.readingQuestionIds ||
              createAttemptResponse.oetTest?.readingQuestionIds,
            writingTaskIds:
              createAttemptResponse.ieltsTest?.writingTaskIds ||
              createAttemptResponse.oetTest?.writingTaskIds,
            // For listening step-by-step part navigation (already sorted above)
            listeningPartsOrder:
              createAttemptResponse.ieltsTest?.listeningSectionIds ||
              createAttemptResponse.oetTest?.listeningPartIds ||
              [],
            currentListeningPartIndex: 0,
            // For reading step-by-step part navigation (not question-by-question, already sorted above)
            readingPartsOrder:
              createAttemptResponse.ieltsTest?.readingSectionIds ||
              createAttemptResponse.oetTest?.readingPartIds ||
              [],
            readingQuestionsOrder:
              createAttemptResponse.ieltsTest?.readingQuestionIds ||
              createAttemptResponse.oetTest?.readingQuestionIds ||
              [],
            currentReadingPartIndex: 0,
            // Include the practice data we just fetched
            practiceData: initialPracticeData,
            // Include test type info for downstream navigation
            testType,
            baseRoute,
            backRoute,
          },
        });
      }
    } catch (error) {
      console.error("Failed to create practice test attempt:", error);

      // Extract error message from backend response
      let errorMessage = "Failed to start practice test. Please try again.";

      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.data?.errorMessage) {
        errorMessage = error.data.errorMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    }
  };

  // ----------- READING PARTS: MAIN CHECKBOXES ALWAYS VISIBLE ---------------
  const stableReadingParts =
    readingParts.length > 0
      ? readingParts
      : initialReadingPartsRef.current &&
        initialReadingPartsRef.current.length > 0
      ? initialReadingPartsRef.current
      : [
          { _id: "A", part: "A" },
          { _id: "B", part: "B" },
          { _id: "C", part: "C" },
        ];

  // You can adjust these question ranges as needed.
  // Dynamically filter questions based on checked parts
  // Use persistent questions if current data is empty but we had questions before
  const currentReadingQuestions = readingData?.data?.readingQuestions || [];
  const readingQuestions =
    currentReadingQuestions.length > 0
      ? currentReadingQuestions
      : persistentReadingQuestions;

  console.log(
    "Current reading questions from API:",
    currentReadingQuestions.length
  );
  console.log(
    "Persistent reading questions:",
    persistentReadingQuestions.length
  );
  console.log("Using reading questions:", readingQuestions.length);
  console.log("stableReadingParts:", stableReadingParts);
  console.log("selectedReadingParts state:", selectedReadingParts);

  const selectedParts = stableReadingParts.filter(
    (p) => selectedReadingParts[p._id]
  );

  console.log("Reading questions available:", readingQuestions.length);
  console.log("Selected reading sections:", selectedParts);
  console.log("Reading questions sample:", readingQuestions.slice(0, 2));
  console.log(
    "All section part names:",
    stableReadingParts.map((p) => ({
      id: p._id,
      part: p.part,
      sectionName: p.sectionName,
    }))
  );

  // Group questions by section dynamically
  let questionsByPart = {};
  if (
    readingQuestions.length &&
    (readingQuestions[0].sectionId || readingQuestions[0].readingSectionId)
  ) {
    // If backend provides a 'sectionId' or 'readingSectionId' property (new IELTS structure)
    console.log("Using 'sectionId/readingSectionId' property from backend");
    for (const p of selectedParts) {
      questionsByPart[p.part] = readingQuestions.filter(
        (q) => q.sectionId === p._id || q.readingSectionId === p._id
      );
      console.log(
        `Section ${p.part} questions:`,
        questionsByPart[p.part].length
      );
    }
  } else if (readingQuestions.length && readingQuestions[0].part) {
    // If backend provides a 'part' property (legacy structure)
    console.log("Using 'part' property from backend");
    for (const p of selectedParts) {
      questionsByPart[p.part] = readingQuestions.filter(
        (q) => q.part === p.part
      );
      console.log(`Part ${p.part} questions:`, questionsByPart[p.part].length);
    }
  } else {
    // Fallback to questionId ranges if no 'part' property
    console.log("Using questionId ranges fallback");
    for (const p of selectedParts) {
      if (p.part === "A") {
        questionsByPart[p.part] = readingQuestions.filter(
          (q) => q.questionId >= 1 && q.questionId <= 20
        );
      } else if (p.part === "B") {
        questionsByPart[p.part] = readingQuestions.filter(
          (q) => q.questionId >= 21 && q.questionId <= 26
        );
      } else if (p.part === "C") {
        questionsByPart[p.part] = readingQuestions.filter(
          (q) => q.questionId >= 27 && q.questionId <= 42
        );
      }
      console.log(
        `Part ${p.part} questions (by ID range):`,
        questionsByPart[p.part]?.length || 0
      );
    }

    // If no questions found with ranges, show all available questions for debugging
    if (
      readingQuestions.length > 0 &&
      Object.values(questionsByPart).every((arr) => arr.length === 0)
    ) {
      console.log(
        "No questions found with ranges. Available questions:",
        readingQuestions.map((q) => ({ id: q.questionId, part: q.part }))
      );
      console.log("Sample question structure:", readingQuestions[0]);
      console.log(
        "Question keys:",
        readingQuestions[0] ? Object.keys(readingQuestions[0]) : "No questions"
      );
      console.log(
        "Selected sections IDs:",
        selectedParts.map((p) => p._id)
      );

      // Group questions by trying different approaches
      console.log(
        `Attempting to divide ${readingQuestions.length} questions among ${selectedParts.length} selected sections`
      );

      // First try: Look for readingSectionId field in questions
      let questionsGrouped = false;
      for (const section of selectedParts) {
        const questionsWithSectionId = readingQuestions.filter(
          (q) =>
            q.readingSectionId === section._id ||
            q.sectionId === section._id ||
            q.section === section._id
        );
        if (questionsWithSectionId.length > 0) {
          questionsByPart[section.part] = questionsWithSectionId;
          console.log(
            `${section.part} questions (by sectionId):`,
            questionsWithSectionId.length
          );
          questionsGrouped = true;
        }
      }

      // Second try: Divide by question number if no sectionId matches
      if (!questionsGrouped) {
        // For IELTS, typically section 1: Q1-13, section 2: Q14-26, section 3: Q27-40
        for (const section of selectedParts) {
          let sectionQuestions = [];
          if (section.sectionName === "section 1") {
            sectionQuestions = readingQuestions.filter(
              (q) => q.questionId >= 1 && q.questionId <= 13
            );
          } else if (section.sectionName === "section 2") {
            sectionQuestions = readingQuestions.filter(
              (q) => q.questionId >= 14 && q.questionId <= 26
            );
          } else if (section.sectionName === "section 3") {
            sectionQuestions = readingQuestions.filter(
              (q) => q.questionId >= 27 && q.questionId <= 40
            );
          }

          if (sectionQuestions.length > 0) {
            questionsByPart[section.part] = sectionQuestions;
            console.log(
              `${section.part} questions (by question number):`,
              sectionQuestions.length
            );
            questionsGrouped = true;
          }
        }
      }

      // Last resort: Divide equally among selected sections only
      if (!questionsGrouped) {
        const questionsPerSection = Math.ceil(
          readingQuestions.length / selectedParts.length
        );

        for (let i = 0; i < selectedParts.length; i++) {
          const section = selectedParts[i];
          const startIndex = i * questionsPerSection;
          const endIndex = Math.min(
            startIndex + questionsPerSection,
            readingQuestions.length
          );
          const sectionQuestions = readingQuestions.slice(startIndex, endIndex);

          questionsByPart[section.part] = sectionQuestions;
          console.log(
            `${section.part} questions (equal division):`,
            sectionQuestions.length
          );
        }
      }
    }
  }

  if (isLoading)
    return (
      <Box p={4} textAlign="center">
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Typography color="error" align="center" p={4}>
        Error loading test
      </Typography>
    );

  return (
    <>
      {/* Instruction System Wrapper */}
      <InstructionWrapper
        instructionState={instructionState}
        onInstructionNext={handleTutorialComplete}
        onInstructionClose={hideInstructionModal}
        onConfirmationConfirm={() => {
          hideConfirmationDialog();
          hideUnattemptedDialog();
        }}
        onConfirmationClose={hideConfirmationDialog}
        onUnattemptedContinue={() => {
          hideUnattemptedDialog();
        }}
        onUnattemptedClose={() => {
          hideUnattemptedDialog();
        }}
        onTimeExpiryContinue={hideTimeExpiryDialog}
        timeRemaining={null}
        unattemptedCount={0}
        nextSection="test"
      />

      <Dialog
        open={open}
        fullWidth
        maxWidth={false}
        TransitionComponent={Grow}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2, md: 4, lg: 5 },
            boxShadow: { xs: 0, sm: 7 },
            background: "#f9fafd",
            px: { xs: 0, sm: 1, md: 2, lg: 2.5 },
            py: { xs: 0, sm: 1, md: 2, lg: 2.5 },
            width: "100%",
            maxWidth: { xs: "100vw", sm: "95vw", md: "90vw", lg: "850px" },
            margin: { xs: 0, sm: 1, md: 2, lg: 3 },
            maxHeight: { xs: "100vh", sm: "95vh", md: "90vh" },
            overflow: "hidden",
            position: { xs: "fixed", sm: "relative" },
            top: { xs: 0, sm: "auto" },
            left: { xs: 0, sm: "auto" },
            right: { xs: 0, sm: "auto" },
            bottom: { xs: 0, sm: "auto" },
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            height: "100%",
            overflow: "auto",
            "&::-webkit-scrollbar": {
              width: { xs: 4, sm: 6 },
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "10px",
            },
          }}
        >
          <Box
            sx={{
              p: { xs: 1, sm: 1.5, md: 2, lg: 3 },
              minHeight: { xs: "100vh", sm: "auto" },
            }}
          >
            <Typography
              align="center"
              fontWeight={600}
              sx={{
                letterSpacing: { xs: 0.05, sm: 0.1 },
                color: "#9D2235",
                mb: { xs: 1, sm: 1.5, md: 2, lg: 3 },
                fontSize: {
                  xs: "1.05rem",
                  sm: "1.2rem",
                  md: "1.35rem",
                  lg: "1.5rem",
                },
                px: { xs: 1, sm: 0 },
                lineHeight: { xs: 1.3, sm: 1.4 },
              }}
            >
              {mockTest?.name || "Mock Test"}
            </Typography>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant={window.innerWidth < 600 ? "scrollable" : "fullWidth"}
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{
                style: {
                  background: "#9D2235",
                  height: { xs: 3, sm: 4 },
                  borderRadius: 2,
                },
              }}
              sx={{
                mb: { xs: 1, sm: 1.5, md: 2, lg: 3 },
                borderRadius: { xs: "8px", sm: "10px" },
                bgcolor: "#fff",
                minHeight: { xs: 40, sm: 48 },
                ".MuiTab-root": {
                  fontWeight: 500,
                  letterSpacing: { xs: 0.1, sm: 0.2 },
                  fontSize: {
                    xs: "0.7rem",
                    sm: "0.85rem",
                    md: "0.95rem",
                    lg: "1.02rem",
                  },
                  textTransform: "none",
                  borderRadius: { xs: "8px", sm: "12px" },
                  minHeight: { xs: 36, sm: 40, md: 44, lg: 46 },
                  padding: {
                    xs: "4px 6px",
                    sm: "6px 10px",
                    md: "10px 14px",
                    lg: "12px 16px",
                  },
                  minWidth: { xs: 60, sm: 80 },
                  "&.Mui-selected": {
                    color: "#9D2235",
                    background: "rgba(157,34,53,0.05)",
                  },
                },
                ".MuiTabs-scrollButtons": {
                  color: "#9D2235",
                  "&.Mui-disabled": {
                    opacity: 0.3,
                  },
                },
              }}
            >
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 0.5, color: "#fff" }}
                    >
                      Practice Mode
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        lineHeight: 1.4,
                        color: "#f0f0f0",
                      }}
                    >
                      • Choose specific sections (Listening, Reading, Writing)
                      <br />
                      • Select individual parts and questions
                      <br />
                      • Set custom timers for each section
                      <br />• Practice at your own pace with flexibility
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
                PopperProps={{
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      bgcolor: "#2c3e50",
                      maxWidth: 280,
                      fontSize: "0.75rem",
                      border: "1px solid #34495e",
                      borderRadius: 2,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                    },
                    "& .MuiTooltip-arrow": {
                      color: "#2c3e50",
                      "&:before": {
                        border: "1px solid #34495e",
                      },
                    },
                  },
                }}
              >
                <Tab
                  label={
                    window.innerWidth < 600 ? "Practice" : "Mode (Practice)"
                  }
                />
              </Tooltip>

              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 0.5, color: "#fff" }}
                    >
                      Simulation Mode
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        lineHeight: 1.4,
                        color: "#f0f0f0",
                      }}
                    >
                      • Complete OET test simulation experience
                      <br />
                      • Follow official test timing and structure
                      <br />
                      • Sequential sections: Listening → Reading → Writing →
                      Speaking
                      <br />• Realistic exam conditions and format
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
                PopperProps={{
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      bgcolor: "#2c3e50",
                      maxWidth: 280,
                      fontSize: "0.75rem",
                      border: "1px solid #34495e",
                      borderRadius: 2,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                    },
                    "& .MuiTooltip-arrow": {
                      color: "#2c3e50",
                      "&:before": {
                        border: "1px solid #34495e",
                      },
                    },
                  },
                }}
              >
                <Tab
                  label={
                    window.innerWidth < 600 ? "Simulation" : "Mode (Simulation)"
                  }
                />
              </Tooltip>

              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 0.5, color: "#fff" }}
                    >
                      Speaking Section
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        lineHeight: 1.4,
                        color: "#f0f0f0",
                      }}
                    >
                      • Role-play scenarios with patients/colleagues
                      <br />
                      • Record responses or upload audio files
                      <br />
                      • Practice professional healthcare communication
                      <br />• Develop empathy and clear speaking skills
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
                PopperProps={{
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      bgcolor: "#2c3e50",
                      maxWidth: 280,
                      fontSize: "0.75rem",
                      border: "1px solid #34495e",
                      borderRadius: 2,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                    },
                    "& .MuiTooltip-arrow": {
                      color: "#2c3e50",
                      "&:before": {
                        border: "1px solid #34495e",
                      },
                    },
                  },
                }}
              >
                <Tab label="Speaking" />
              </Tooltip>

              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 0.5, color: "#fff" }}
                    >
                      Mock Test PDFs
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        lineHeight: 1.4,
                        color: "#f0f0f0",
                      }}
                    >
                      • Download complete test materials
                      <br />
                      • Access reading passages and question papers
                      <br />
                      • Print for offline practice sessions
                      <br />• Reference materials for comprehensive study
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
                PopperProps={{
                  sx: {
                    "& .MuiTooltip-tooltip": {
                      bgcolor: "#2c3e50",
                      maxWidth: 280,
                      fontSize: "0.75rem",
                      border: "1px solid #34495e",
                      borderRadius: 2,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                    },
                    "& .MuiTooltip-arrow": {
                      color: "#2c3e50",
                      "&:before": {
                        border: "1px solid #34495e",
                      },
                    },
                  },
                }}
              >
                <Tab
                  label={window.innerWidth < 600 ? "PDFs" : "Mock Test PDFs"}
                />
              </Tooltip>
            </Tabs>
            {/* PRACTICE TAB */}
            {tab === 0 && (
              <Box
                sx={{
                  width: "100%",
                  px: { xs: 0.5, sm: 0 },
                }}
              >
                <Typography
                  fontWeight={600}
                  gutterBottom
                  fontSize={{
                    xs: "0.85rem",
                    sm: "0.95rem",
                    md: "1.05rem",
                    lg: "1.08rem",
                  }}
                  sx={{
                    mb: { xs: 0.75, sm: 1, md: 1.5 },
                    px: { xs: 0.5, sm: 0 },
                  }}
                >
                  1. Choose mode you want to practice:
                </Typography>
                <Select
                  multiple
                  displayEmpty
                  value={Object.keys(modeSelection).filter(
                    (k) => modeSelection[k]
                  )}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setModeSelection((prev) => ({
                      listening: newValue.includes("listening"),
                      reading: newValue.includes("reading"),
                      writing: newValue.includes("writing"),
                    }));
                  }}
                  renderValue={(selected) => {
                    if (!selected.length)
                      return (
                        <span
                          style={{
                            color: "#aaa",
                            fontSize:
                              window.innerWidth < 600
                                ? "0.8rem"
                                : window.innerWidth < 900
                                ? "0.9rem"
                                : "1rem",
                          }}
                        >
                          Select sections
                        </span>
                      );
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: { xs: 0.25, sm: 0.5 },
                          py: { xs: 0.25, sm: 0 },
                        }}
                      >
                        {selected.map((key) => (
                          <Chip
                            key={key}
                            label={key.charAt(0).toUpperCase() + key.slice(1)}
                            onDelete={(e) => handleChipDelete(e, key)}
                            // Prevent mouseDown on delete icon from bubbling — this is the crucial part
                            deleteIcon={
                              <span
                                onMouseDown={(ev) => {
                                  ev.stopPropagation();
                                }}
                              >
                                <CloseIcon
                                  sx={{ fontSize: "1rem !important" }}
                                />
                              </span>
                            }
                            sx={{
                              borderRadius: "999px",
                              fontSize: {
                                xs: "0.7rem",
                                sm: "0.8rem",
                                md: "0.875rem",
                              },
                              height: { xs: 20, sm: 24, md: 28, lg: 32 },
                              "& .MuiChip-label": {
                                px: { xs: 0.75, sm: 1 },
                              },
                              "& .MuiChip-deleteIcon": {
                                fontSize: { xs: "0.8rem", sm: "0.9rem" },
                                color: "#666",
                                "&:hover": {
                                  color: "#333",
                                },
                              },
                            }}
                            size="small"
                          />
                        ))}
                      </Box>
                    );
                  }}
                  sx={{
                    mb: { xs: 1, sm: 1.5, md: 2 },
                    width: "100%",
                    minWidth: { xs: 0, sm: 220 },
                    "& .MuiSelect-select": {
                      fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                      padding: {
                        xs: "6px 10px",
                        sm: "10px 14px",
                        md: "14px 16px",
                      },
                      minHeight: { xs: "1.2em", sm: "1.4375em" },
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: { xs: 1, sm: 1.5 },
                    },
                  }}
                  size="small"
                >
                  {[
                    ["listening", "Listening"],
                    ["reading", "Reading"],
                    ["writing", "Writing"],
                  ].map(([key, label]) => {
                    const isAvailable = availableModules[key];

                    const menuItem = (
                      <MenuItem
                        key={key}
                        value={key}
                        disabled={!isAvailable}
                        sx={{
                          fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                          py: { xs: 0.5, sm: 0.75, md: 1 },
                          px: { xs: 1, sm: 1.5 },
                          minHeight: { xs: 36, sm: 40 },
                          opacity: isAvailable ? 1 : 0.5,
                          cursor: isAvailable ? "pointer" : "not-allowed",
                          backgroundColor: !isAvailable
                            ? "rgba(0,0,0,0.04)"
                            : "transparent",
                        }}
                      >
                        <Checkbox
                          checked={!!modeSelection[key]}
                          disabled={!isAvailable}
                          size="small"
                          sx={{
                            "& .MuiSvgIcon-root": {
                              fontSize: { xs: "1.1rem", sm: "1.25rem" },
                            },
                          }}
                        />
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {label}
                              {!isAvailable && (
                                <Chip
                                  label="Not in package"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: "0.65rem",
                                    backgroundColor: "#ffebee",
                                    color: "#c62828",
                                    fontWeight: 500,
                                  }}
                                />
                              )}
                            </Box>
                          }
                          primaryTypographyProps={{
                            fontSize: {
                              xs: "0.8rem",
                              sm: "0.9rem",
                              md: "1rem",
                            },
                            fontWeight: 500,
                          }}
                          sx={{ ml: { xs: 0.5, sm: 1 } }}
                        />
                      </MenuItem>
                    );

                    // Only wrap with Tooltip if module is not available
                    if (!isAvailable) {
                      return (
                        <Tooltip
                          key={key}
                          title={
                            <Box sx={{ p: 0.5, textAlign: "center" }}>
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  color: "#fff",
                                }}
                              >
                                🔒 Module Not Available
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  color: "#f0f0f0",
                                  mt: 0.5,
                                }}
                              >
                                Your package does not include {label}
                              </Typography>
                            </Box>
                          }
                          placement="right"
                          arrow
                          PopperProps={{
                            sx: {
                              "& .MuiTooltip-tooltip": {
                                bgcolor: "#d32f2f",
                                maxWidth: 220,
                                borderRadius: 2,
                                boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                              },
                              "& .MuiTooltip-arrow": {
                                color: "#d32f2f",
                              },
                            },
                          }}
                        >
                          <span>{menuItem}</span>
                        </Tooltip>
                      );
                    }

                    return menuItem;
                  })}
                </Select>
                {Object.values(modeSelection).some(Boolean) && (
                  <Box
                    sx={{
                      mb: { xs: 1.5, sm: 2, md: 3 },
                      px: { xs: 0.5, sm: 0 },
                    }}
                  >
                    <Typography
                      fontWeight={600}
                      gutterBottom
                      fontSize={{
                        xs: "0.85rem",
                        sm: "0.95rem",
                        md: "1.05rem",
                        lg: "1.08rem",
                      }}
                      sx={{ mb: { xs: 0.75, sm: 1, md: 1.5 } }}
                    >
                      2. Set timers for selected sections:
                    </Typography>

                    {/* Note about Listening timer */}
                    {modeSelection.listening && (
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1.5,
                          color: "text.secondary",
                          fontSize: {
                            xs: "0.75rem",
                            sm: "0.8rem",
                            md: "0.85rem",
                          },
                        }}
                      >
                        * Listening section uses audio duration as timer in
                        practice mode
                      </Typography>
                    )}

                    {/* Reading Timer */}
                    {modeSelection.reading && (
                      <Box
                        sx={{ mb: 2, display: "flex", alignItems: "center" }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            minWidth: { xs: 70, sm: 80, md: 90 },
                            fontSize: {
                              xs: "0.8rem",
                              sm: "0.85rem",
                              md: "0.9rem",
                            },
                          }}
                        >
                          Reading:
                        </Typography>
                        <Select
                          value={readingMinutes}
                          onChange={(e) => setReadingMinutes(e.target.value)}
                          sx={{
                            width: { xs: "100%", sm: "75%", md: 200, lg: 180 },
                            "& .MuiSelect-select": {
                              fontSize: {
                                xs: "0.8rem",
                                sm: "0.9rem",
                                md: "1rem",
                              },
                              padding: {
                                xs: "6px 10px",
                                sm: "8px 12px",
                                md: "10px 14px",
                              },
                            },
                            "& .MuiOutlinedInput-root": {
                              borderRadius: { xs: 1, sm: 1.5 },
                            },
                          }}
                          size="small"
                        >
                          {Array.from({ length: 120 }, (_, i) => (
                            <MenuItem
                              key={i + 1}
                              value={i + 1}
                              sx={{
                                fontSize: {
                                  xs: "0.8rem",
                                  sm: "0.9rem",
                                  md: "1rem",
                                },
                                py: { xs: 0.5, sm: 0.75 },
                                minHeight: { xs: 32, sm: 36 },
                              }}
                            >
                              {i + 1} minute{i === 0 ? "" : "s"}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    )}

                    {/* Writing Timer */}
                    {modeSelection.writing && (
                      <Box
                        sx={{ mb: 2, display: "flex", alignItems: "center" }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            minWidth: { xs: 70, sm: 80, md: 90 },
                            fontSize: {
                              xs: "0.8rem",
                              sm: "0.85rem",
                              md: "0.9rem",
                            },
                          }}
                        >
                          Writing:
                        </Typography>
                        <Select
                          value={writingMinutes}
                          onChange={(e) => setWritingMinutes(e.target.value)}
                          sx={{
                            width: { xs: "100%", sm: "75%", md: 200, lg: 180 },
                            "& .MuiSelect-select": {
                              fontSize: {
                                xs: "0.8rem",
                                sm: "0.9rem",
                                md: "1rem",
                              },
                              padding: {
                                xs: "6px 10px",
                                sm: "8px 12px",
                                md: "10px 14px",
                              },
                            },
                            "& .MuiOutlinedInput-root": {
                              borderRadius: { xs: 1, sm: 1.5 },
                            },
                          }}
                          size="small"
                        >
                          {Array.from({ length: 120 }, (_, i) => (
                            <MenuItem
                              key={i + 1}
                              value={i + 1}
                              sx={{
                                fontSize: {
                                  xs: "0.8rem",
                                  sm: "0.9rem",
                                  md: "1rem",
                                },
                                py: { xs: 0.5, sm: 0.75 },
                                minHeight: { xs: 32, sm: 36 },
                              }}
                            >
                              {i + 1} minute{i === 0 ? "" : "s"}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    )}

                    {/* Speaking Timer */}
                    {modeSelection.speaking && (
                      <Box
                        sx={{ mb: 2, display: "flex", alignItems: "center" }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            minWidth: { xs: 70, sm: 80, md: 90 },
                            fontSize: {
                              xs: "0.8rem",
                              sm: "0.85rem",
                              md: "0.9rem",
                            },
                          }}
                        >
                          Speaking:
                        </Typography>
                        <Select
                          value={speakingMinutes}
                          onChange={(e) => setSpeakingMinutes(e.target.value)}
                          sx={{
                            width: { xs: "100%", sm: "75%", md: 200, lg: 180 },
                            "& .MuiSelect-select": {
                              fontSize: {
                                xs: "0.8rem",
                                sm: "0.9rem",
                                md: "1rem",
                              },
                              padding: {
                                xs: "6px 10px",
                                sm: "8px 12px",
                                md: "10px 14px",
                              },
                            },
                            "& .MuiOutlinedInput-root": {
                              borderRadius: { xs: 1, sm: 1.5 },
                            },
                          }}
                          size="small"
                        >
                          {Array.from({ length: 120 }, (_, i) => (
                            <MenuItem
                              key={i + 1}
                              value={i + 1}
                              sx={{
                                fontSize: {
                                  xs: "0.8rem",
                                  sm: "0.9rem",
                                  md: "1rem",
                                },
                                py: { xs: 0.5, sm: 0.75 },
                                minHeight: { xs: 32, sm: 36 },
                              }}
                            >
                              {i + 1} minute{i === 0 ? "" : "s"}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    )}

                    <Typography
                      color="text.secondary"
                      sx={{
                        mt: { xs: 0.5, sm: 0.75, md: 1 },
                        fontSize: {
                          xs: "0.7rem",
                          sm: "0.8rem",
                          md: "0.875rem",
                        },
                        lineHeight: 1.3,
                        px: { xs: 0.5, sm: 0 },
                      }}
                    >
                      Separate timers for each section in practice mode
                    </Typography>
                  </Box>
                )}
                {modeSelection.listening && listeningParts.length > 0 && (
                  <Box
                    sx={{
                      mb: { xs: 1, sm: 1.5, md: 2 },
                      px: { xs: 0.5, sm: 0 },
                    }}
                  >
                    <Typography
                      fontWeight="bold"
                      gutterBottom
                      sx={{
                        fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                        mb: { xs: 0.5, sm: 0.75 },
                      }}
                    >
                      Listening Sections:
                    </Typography>
                    {loadingListening && !listeningParts.length ? (
                      <Typography
                        sx={{
                          ml: { xs: 1, sm: 2 },
                          fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                          color: "text.secondary",
                        }}
                      >
                        Loading sections…
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          flexWrap: "wrap",
                          gap: { xs: 0.25, sm: 0.5, md: 1 },
                          ml: { xs: 0, sm: 0.5 },
                        }}
                      >
                        {listeningParts.map((p) => (
                          <FormControlLabel
                            key={p._id}
                            control={
                              <Checkbox
                                checked={!!selectedListeningParts[p._id]}
                                onChange={handlePartToggle("listening", p._id)}
                                size="small"
                                sx={{
                                  p: { xs: 0.5, sm: 1 },
                                  "& .MuiSvgIcon-root": {
                                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                                  },
                                }}
                              />
                            }
                            label={p.sectionName || `Section ${p.part}`}
                            sx={{
                              m: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: {
                                  xs: "0.8rem",
                                  sm: "0.9rem",
                                  md: "1rem",
                                },
                                fontWeight: 500,
                                ml: { xs: 0.25, sm: 0.5 },
                              },
                            }}
                          />
                        ))}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                listeningParts.length > 0 &&
                                listeningParts.every(
                                  (p) => selectedListeningParts[p._id]
                                )
                              }
                              indeterminate={
                                listeningParts.some(
                                  (p) => selectedListeningParts[p._id]
                                ) &&
                                !listeningParts.every(
                                  (p) => selectedListeningParts[p._id]
                                )
                              }
                              onChange={(e) => {
                                const allSelected = listeningParts.every(
                                  (p) => selectedListeningParts[p._id]
                                );
                                const newSelection = {};
                                listeningParts.forEach((p) => {
                                  newSelection[p._id] = !allSelected;
                                });
                                setSelectedListeningParts(newSelection);
                              }}
                              size="small"
                              sx={{
                                p: { xs: 0.5, sm: 1 },
                                "& .MuiSvgIcon-root": {
                                  fontSize: { xs: "1.1rem", sm: "1.25rem" },
                                },
                              }}
                            />
                          }
                          label="Select All"
                          sx={{
                            m: 0,
                            "& .MuiFormControlLabel-label": {
                              fontSize: {
                                xs: "0.75rem",
                                sm: "0.85rem",
                                md: "0.9rem",
                              },
                              fontWeight: 500,
                              ml: { xs: 0.25, sm: 0.5 },
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                )}
                {modeSelection.reading && (
                  <Box
                    sx={{
                      mb: { xs: 1, sm: 1.5, md: 2 },
                      px: { xs: 0.5, sm: 0 },
                    }}
                  >
                    <Typography
                      fontWeight="bold"
                      gutterBottom
                      sx={{
                        fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                        mb: { xs: 0.5, sm: 0.75 },
                      }}
                    >
                      Reading Sections:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        flexWrap: "wrap",
                        gap: { xs: 0.25, sm: 0.5, md: 1 },
                        mb: { xs: 0.5, sm: 0.75, md: 1 },
                        ml: { xs: 0, sm: 0.5 },
                      }}
                    >
                      {stableReadingParts.map((p) => (
                        <FormControlLabel
                          key={p._id}
                          control={
                            <Checkbox
                              checked={!!selectedReadingParts[p._id]}
                              onChange={handlePartToggle("reading", p._id)}
                              size="small"
                              sx={{
                                p: { xs: 0.5, sm: 1 },
                                "& .MuiSvgIcon-root": {
                                  fontSize: { xs: "1.1rem", sm: "1.25rem" },
                                },
                              }}
                            />
                          }
                          label={p.sectionName || `Section ${p.part}`}
                          sx={{
                            m: 0,
                            "& .MuiFormControlLabel-label": {
                              fontSize: {
                                xs: "0.8rem",
                                sm: "0.9rem",
                                md: "1rem",
                              },
                              fontWeight: 500,
                              ml: { xs: 0.25, sm: 0.5 },
                            },
                          }}
                        />
                      ))}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              stableReadingParts.length > 0 &&
                              stableReadingParts.every(
                                (p) => selectedReadingParts[p._id]
                              )
                            }
                            indeterminate={
                              stableReadingParts.some(
                                (p) => selectedReadingParts[p._id]
                              ) &&
                              !stableReadingParts.every(
                                (p) => selectedReadingParts[p._id]
                              )
                            }
                            onChange={(e) => {
                              const allSelected = stableReadingParts.every(
                                (p) => selectedReadingParts[p._id]
                              );
                              const newSelection = {};
                              const newQuestions = {};
                              stableReadingParts.forEach((p) => {
                                newSelection[p._id] = !allSelected;
                                // Auto-select/deselect questions for each part
                                if (questionsByPart[p.part]) {
                                  questionsByPart[p.part].forEach((q) => {
                                    newQuestions[q._id] = !allSelected;
                                  });
                                }
                              });
                              setSelectedReadingParts(newSelection);
                              setSelectedReadingQuestions((prev) => ({
                                ...prev,
                                ...newQuestions,
                              }));
                            }}
                            size="small"
                            sx={{
                              p: { xs: 0.5, sm: 1 },
                              "& .MuiSvgIcon-root": {
                                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                              },
                            }}
                          />
                        }
                        label="Select All"
                        sx={{
                          m: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: {
                              xs: "0.75rem",
                              sm: "0.85rem",
                              md: "0.9rem",
                            },
                            fontWeight: 500,
                            ml: { xs: 0.25, sm: 0.5 },
                          },
                        }}
                      />
                    </Box>
                    {selectedParts.map(
                      (p) =>
                        questionsByPart[p.part] &&
                        questionsByPart[p.part].length > 0 && (
                          <Box
                            key={p.part}
                            sx={{
                              ml: { xs: 0.5, sm: 1.5, md: 3, lg: 5 },
                              mb: { xs: 0.5, sm: 0.75, md: 1 },
                              px: { xs: 0.5, sm: 0 },
                            }}
                          >
                            <Typography
                              fontWeight={600}
                              sx={{
                                mb: { xs: 0.25, sm: 0.375, md: 0.5 },
                                fontSize: {
                                  xs: "0.8rem",
                                  sm: "0.9rem",
                                  md: "1rem",
                                },
                                color: "#333",
                              }}
                            >
                              {p.sectionName || p.part} Questions:
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: { xs: 0.25, sm: 0.5, md: 1 },
                                maxWidth: "100%",
                              }}
                            >
                              {questionsByPart[p.part].map((q) => (
                                <FormControlLabel
                                  key={q._id}
                                  control={
                                    <Checkbox
                                      checked={
                                        !!selectedReadingQuestions[q._id]
                                      }
                                      onChange={handlePartToggle(
                                        "readingQuestion",
                                        q._id
                                      )}
                                      size="small"
                                      sx={{
                                        p: { xs: 0.25, sm: 0.5 },
                                        "& .MuiSvgIcon-root": {
                                          fontSize: {
                                            xs: "1rem",
                                            sm: "1.1rem",
                                          },
                                        },
                                      }}
                                    />
                                  }
                                  label={`Q${q.questionId}`}
                                  sx={{
                                    minWidth: { xs: 55, sm: 60, md: 65 },
                                    m: 0,
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: {
                                        xs: "0.7rem",
                                        sm: "0.8rem",
                                        md: "0.875rem",
                                      },
                                      fontWeight: 500,
                                      ml: { xs: 0.25, sm: 0.5 },
                                    },
                                  }}
                                />
                              ))}
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={
                                      questionsByPart[p.part].length > 0 &&
                                      questionsByPart[p.part].every(
                                        (q) => selectedReadingQuestions[q._id]
                                      )
                                    }
                                    indeterminate={
                                      questionsByPart[p.part].some(
                                        (q) => selectedReadingQuestions[q._id]
                                      ) &&
                                      !questionsByPart[p.part].every(
                                        (q) => selectedReadingQuestions[q._id]
                                      )
                                    }
                                    onChange={(e) => {
                                      const allSelected = questionsByPart[
                                        p.part
                                      ].every(
                                        (q) => selectedReadingQuestions[q._id]
                                      );
                                      const newSelection = {
                                        ...selectedReadingQuestions,
                                      };
                                      questionsByPart[p.part].forEach((q) => {
                                        newSelection[q._id] = !allSelected;
                                      });
                                      setSelectedReadingQuestions(newSelection);
                                    }}
                                    size="small"
                                    sx={{
                                      p: { xs: 0.25, sm: 0.5 },
                                      "& .MuiSvgIcon-root": {
                                        fontSize: { xs: "1rem", sm: "1.1rem" },
                                      },
                                    }}
                                  />
                                }
                                label="Select All"
                                sx={{
                                  minWidth: { xs: 70, sm: 75, md: 80 },
                                  m: 0,
                                  "& .MuiFormControlLabel-label": {
                                    fontSize: {
                                      xs: "0.7rem",
                                      sm: "0.75rem",
                                      md: "0.8rem",
                                    },
                                    fontWeight: 500,
                                    ml: { xs: 0.25, sm: 0.5 },
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                        )
                    )}
                  </Box>
                )}
                {modeSelection.writing && writingTasks.length > 0 && (
                  <Box
                    sx={{
                      mb: { xs: 1, sm: 1.5, md: 2 },
                      px: { xs: 0.5, sm: 0 },
                    }}
                  >
                    <Typography
                      fontWeight="bold"
                      gutterBottom
                      sx={{
                        fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                        mb: { xs: 0.5, sm: 0.75 },
                      }}
                    >
                      Writing Tasks:
                    </Typography>
                    {loadingWriting && !writingTasks.length ? (
                      <Typography
                        sx={{
                          ml: { xs: 1, sm: 2 },
                          fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                          color: "text.secondary",
                        }}
                      >
                        Loading tasks…
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          flexWrap: "wrap",
                          gap: { xs: 0.25, sm: 0.5, md: 1 },
                          ml: { xs: 0, sm: 0.5 },
                        }}
                      >
                        {writingTasks.map((task) => (
                          <FormControlLabel
                            key={task._id}
                            control={
                              <Checkbox
                                checked={!!selectedWritingTasks[task._id]}
                                onChange={handlePartToggle("writing", task._id)}
                                size="small"
                                sx={{
                                  p: { xs: 0.5, sm: 1 },
                                  "& .MuiSvgIcon-root": {
                                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                                  },
                                }}
                              />
                            }
                            label={task.taskTitle || `Task ${task.taskNumber}`}
                            sx={{
                              m: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: {
                                  xs: "0.8rem",
                                  sm: "0.9rem",
                                  md: "1rem",
                                },
                                fontWeight: 500,
                                ml: { xs: 0.25, sm: 0.5 },
                              },
                            }}
                          />
                        ))}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                writingTasks.length > 0 &&
                                writingTasks.every(
                                  (task) => selectedWritingTasks[task._id]
                                )
                              }
                              indeterminate={
                                writingTasks.some(
                                  (task) => selectedWritingTasks[task._id]
                                ) &&
                                !writingTasks.every(
                                  (task) => selectedWritingTasks[task._id]
                                )
                              }
                              onChange={(e) => {
                                const allSelected = writingTasks.every(
                                  (task) => selectedWritingTasks[task._id]
                                );
                                const newSelection = {};
                                writingTasks.forEach((task) => {
                                  newSelection[task._id] = !allSelected;
                                });
                                setSelectedWritingTasks(newSelection);
                              }}
                              size="small"
                              sx={{
                                p: { xs: 0.5, sm: 1 },
                                "& .MuiSvgIcon-root": {
                                  fontSize: { xs: "1.1rem", sm: "1.25rem" },
                                },
                              }}
                            />
                          }
                          label="Select All"
                          sx={{
                            m: 0,
                            "& .MuiFormControlLabel-label": {
                              fontSize: {
                                xs: "0.75rem",
                                sm: "0.85rem",
                                md: "0.9rem",
                              },
                              fontWeight: 500,
                              ml: { xs: 0.25, sm: 0.5 },
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                )}
                <Divider
                  sx={{
                    my: { xs: 1, sm: 1.5, md: 2, lg: 3 },
                    mx: { xs: -0.5, sm: 0 },
                  }}
                />
                <DialogActions
                  sx={{
                    p: 0,
                    mt: { xs: 0.75, sm: 1, md: 1.5, lg: 2 },
                    px: { xs: 0.5, sm: 0 },
                    justifyContent: {
                      xs: "space-between",
                      sm: "space-between",
                    },
                    gap: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{
                      borderColor: "#9D2235",
                      color: "#9D2235",
                      borderRadius: { xs: "12px", sm: "16px", md: "18px" },
                      fontWeight: 600,
                      fontSize: {
                        xs: "0.85rem",
                        sm: "0.95rem",
                        md: "1.05rem",
                        lg: "1.08rem",
                      },
                      px: { xs: 2.5, sm: 3.5, md: 5, lg: 6 },
                      py: { xs: 0.9, sm: 1, md: 1.2, lg: 1.3 },
                      minHeight: { xs: 40, sm: 44, md: 48 },
                      textTransform: "uppercase",
                      letterSpacing: { xs: 0.5, sm: 1 },
                      "&:hover": {
                        borderColor: "#6d1723",
                        backgroundColor: "rgba(157, 34, 53, 0.05)",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleFinishSelection}
                    disabled={
                      loadingListening || loadingReading || loadingWriting
                    }
                    sx={{
                      background: "#9D2235",
                      borderRadius: { xs: "12px", sm: "16px", md: "18px" },
                      fontWeight: 700,
                      fontSize: {
                        xs: "0.85rem",
                        sm: "0.95rem",
                        md: "1.05rem",
                        lg: "1.08rem",
                      },
                      px: { xs: 2.5, sm: 3.5, md: 5, lg: 6 },
                      py: { xs: 0.9, sm: 1, md: 1.2, lg: 1.3 },
                      boxShadow: { xs: 1, sm: 2 },
                      minHeight: { xs: 40, sm: 44, md: 48 },
                      textTransform: "uppercase",
                      letterSpacing: { xs: 0.5, sm: 1 },
                      "&:hover": {
                        background: "#6d1723",
                        boxShadow: { xs: 2, sm: 3 },
                      },
                      "&:disabled": {
                        background: "#ccc",
                        color: "#888",
                      },
                    }}
                  >
                    {loadingListening || loadingReading || loadingWriting
                      ? "Loading..."
                      : "START"}
                  </Button>
                </DialogActions>
              </Box>
            )}
            {/* SIMULATION TAB */}
            {tab === 1 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  px: { xs: 1, sm: 2, md: 4, lg: 6 },
                  py: { xs: 1.5, sm: 2 },
                  minHeight: { xs: 250, sm: 300, md: 370, lg: 390 },
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    background: "#f9ecec",
                    borderRadius: "50%",
                    width: { xs: 56, sm: 72, md: 84 },
                    height: { xs: 56, sm: 72, md: 84 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: { xs: 1.5, sm: 2, md: 3 },
                  }}
                >
                  <AssignmentOutlinedIcon
                    sx={{
                      fontSize: { xs: 32, sm: 40, md: 48 },
                      color: "#9E4646",
                    }}
                  />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 550,
                    mb: { xs: 1.5, sm: 2 },
                    color: "#222",
                    textAlign: "center",
                    fontSize: { xs: "1rem", sm: "1.07rem", md: "1.16rem" },
                  }}
                >
                  IELTS Simulation Test
                </Typography>

                {/* Module availability indicators */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 0.75, sm: 1 },
                    justifyContent: "center",
                    mb: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 0 },
                  }}
                >
                  {[
                    { key: "listening", label: "Listening", icon: "🎧" },
                    { key: "reading", label: "Reading", icon: "📖" },
                    { key: "writing", label: "Writing", icon: "✍️" },
                    // Speaking is not included in simulation mode
                  ].map(({ key, label, icon }) => (
                    <Tooltip
                      key={key}
                      title={
                        !availableModules[key] ? (
                          <Box sx={{ p: 0.5, textAlign: "center" }}>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 600,
                                color: "#fff",
                              }}
                            >
                              🔒 Not Included
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: "#f0f0f0",
                                mt: 0.5,
                              }}
                            >
                              {label} is not part of your package
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ p: 0.5, textAlign: "center" }}>
                            <Typography
                              sx={{
                                fontSize: "0.8rem",
                                fontWeight: 600,
                                color: "#fff",
                              }}
                            >
                              ✓ Available
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                color: "#f0f0f0",
                                mt: 0.5,
                              }}
                            >
                              {label} is included in your package
                            </Typography>
                          </Box>
                        )
                      }
                      arrow
                      placement="top"
                      PopperProps={{
                        sx: {
                          "& .MuiTooltip-tooltip": {
                            bgcolor: availableModules[key]
                              ? "#2e7d32"
                              : "#d32f2f",
                            maxWidth: 200,
                            borderRadius: 2,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                          },
                          "& .MuiTooltip-arrow": {
                            color: availableModules[key]
                              ? "#2e7d32"
                              : "#d32f2f",
                          },
                        },
                      }}
                    >
                      <Chip
                        icon={
                          <span style={{ fontSize: "0.9rem" }}>
                            {availableModules[key] ? icon : "🔒"}
                          </span>
                        }
                        label={label}
                        size="small"
                        sx={{
                          backgroundColor: availableModules[key]
                            ? "#e8f5e9"
                            : "#ffebee",
                          color: availableModules[key] ? "#2e7d32" : "#c62828",
                          fontWeight: 600,
                          fontSize: { xs: "0.75rem", sm: "0.85rem" },
                          opacity: availableModules[key] ? 1 : 0.8,
                          border: `2px solid ${
                            availableModules[key] ? "#4caf50" : "#ef5350"
                          }`,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                            boxShadow: availableModules[key]
                              ? "0 2px 8px rgba(76, 175, 80, 0.4)"
                              : "0 2px 8px rgba(239, 83, 80, 0.4)",
                          },
                          "& .MuiChip-icon": {
                            marginLeft: "8px",
                          },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>

                {/* Show which module will start first */}
                {availableSimSections.length > 0 && (
                  <Typography
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      color: "#666",
                      mb: 1,
                      textAlign: "center",
                      fontStyle: "italic",
                    }}
                  >
                    Test will start with:{" "}
                    <b style={{ color: "#9E4646" }}>
                      {availableSimSections[0]?.label}
                    </b>
                  </Typography>
                )}

                <Typography
                  sx={{
                    fontSize: { xs: "0.9rem", sm: "1rem", md: "1.09rem" },
                    color: "#444",
                    mb: { xs: 1.5, sm: 2 },
                    maxWidth: { xs: 320, sm: 400, md: 440 },
                    mx: "auto",
                    textAlign: "center",
                    lineHeight: { xs: 1.5, sm: 1.7 },
                    px: { xs: 1, sm: 0 },
                  }}
                >
                  <b>Listen carefully</b>, audio plays once – answer as you go.
                  <br />
                  Read questions first, manage time well,{" "}
                  <b>choose answers carefully</b>.<br />
                  Write clearly and professionally; <b>speak</b> with empathy
                  and purpose.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    mt: { xs: 0.5, sm: 1 },
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{
                      borderColor: "#9E4646",
                      color: "#9E4646",
                      borderRadius: "18px",
                      px: { xs: 3, sm: 4, md: 6 },
                      py: { xs: 1, sm: 1.1, md: 1.6 },
                      fontWeight: 600,
                      fontSize: { xs: "0.9rem", sm: "1rem", md: "1.17rem" },
                      textTransform: "none",
                      minHeight: { xs: 36, sm: 42, md: 48 },
                      "&:hover": {
                        borderColor: "#7a2323",
                        backgroundColor: "rgba(158, 70, 70, 0.05)",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Tooltip
                    title={
                      !Object.values(availableModules).some(Boolean) ? (
                        <Box sx={{ p: 0.75, textAlign: "center" }}>
                          <Typography
                            sx={{
                              fontSize: "0.85rem",
                              fontWeight: 600,
                              color: "#fff",
                            }}
                          >
                            🔒 No Modules Available
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: "#f0f0f0",
                              mt: 0.5,
                            }}
                          >
                            Your package does not include any test modules
                          </Typography>
                        </Box>
                      ) : (
                        ""
                      )
                    }
                    placement="top"
                    arrow
                    PopperProps={{
                      sx: {
                        "& .MuiTooltip-tooltip": {
                          bgcolor: "#d32f2f",
                          maxWidth: 260,
                          borderRadius: 2,
                          boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                        },
                        "& .MuiTooltip-arrow": {
                          color: "#d32f2f",
                        },
                      },
                    }}
                  >
                    <span>
                      <Button
                        variant="contained"
                        sx={{
                          background: Object.values(availableModules).some(
                            Boolean
                          )
                            ? "#9E4646"
                            : "#ccc",
                          color: "#fff",
                          borderRadius: "18px",
                          px: { xs: 4, sm: 5, md: 7 },
                          py: { xs: 1, sm: 1.1, md: 1.6 },
                          fontWeight: 600,
                          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.17rem" },
                          textTransform: "none",
                          boxShadow: "none",
                          minHeight: { xs: 36, sm: 42, md: 48 },
                          "&:hover": {
                            background: Object.values(availableModules).some(
                              Boolean
                            )
                              ? "#7a2323"
                              : "#ccc",
                          },
                          "&.Mui-disabled": {
                            background: "#ccc",
                            color: "#fff",
                          },
                        }}
                        onClick={handleSimSection}
                        disabled={
                          simulationLoading ||
                          isLoading ||
                          !mockTest ||
                          !Object.values(availableModules).some(Boolean)
                        }
                      >
                        {simulationLoading || isLoading
                          ? "Loading..."
                          : availableSimSections.length > 0
                          ? `Start ${availableSimSections[0]?.label}`
                          : "Start"}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            )}
            {/* SPEAKING TAB */}
            {tab === 2 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  px: { xs: 1, sm: 2, md: 4, lg: 6 },
                  py: { xs: 1.5, sm: 2 },
                  minHeight: { xs: 250, sm: 300, md: 370, lg: 390 },
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    background: "#f9ecec",
                    borderRadius: "50%",
                    width: { xs: 64, sm: 84 },
                    height: { xs: 64, sm: 84 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: { xs: 2, sm: 3 },
                  }}
                >
                  <RecordVoiceOverOutlinedIcon
                    sx={{ fontSize: { xs: 42, sm: 54 }, color: "#9E4646" }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "1.08rem", sm: "1.14rem" },
                    mb: 1.5,
                    textAlign: "center",
                  }}
                >
                  Ielts  Speaking Test – Summary Instructions
                </Typography>
                <Typography
                  sx={{
                    color: "#888",
                    fontSize: { xs: "0.98rem", sm: "1rem" },
                    textAlign: "center",
                    maxWidth: 470,
                    mb: 2,
                    lineHeight: 1.8,
                  }}
                >
          
                  Speak clearly and naturally.
                  <br />
                  Express your ideas confidently.
                  <br />
                  Respond fully to the situation.
                  <br />
                  Use appropriate vocabulary and grammar.
                  <br />
                  Maintain fluency and coherence throughout.
                </Typography>
                <Box
                  sx={{ display: "flex", gap: 2, alignItems: "center", mt: 1 }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{
                      borderColor: "#9E4646",
                      color: "#9E4646",
                      borderRadius: "16px",
                      fontWeight: 600,
                      fontSize: { xs: "1.07rem", sm: "1.18rem" },
                      px: { xs: 4, sm: 7 },
                      py: { xs: 1.1, sm: 1.6 },
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "#7a2323",
                        backgroundColor: "rgba(158, 70, 70, 0.05)",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Tooltip
                    title={
                      !availableModules.speaking ? (
                        <Box sx={{ p: 0.75, textAlign: "center" }}>
                          <Typography
                            sx={{
                              fontSize: "0.85rem",
                              fontWeight: 600,
                              color: "#fff",
                            }}
                          >
                            🔒 Module Not Available
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: "#f0f0f0",
                              mt: 0.5,
                            }}
                          >
                            Speaking is not included in your current package
                          </Typography>
                        </Box>
                      ) : (
                        ""
                      )
                    }
                    placement="top"
                    arrow
                    PopperProps={{
                      sx: {
                        "& .MuiTooltip-tooltip": {
                          bgcolor: "#d32f2f",
                          maxWidth: 240,
                          borderRadius: 2,
                          boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                        },
                        "& .MuiTooltip-arrow": {
                          color: "#d32f2f",
                        },
                      },
                    }}
                  >
                    <span>
                      <Button
                        variant="contained"
                        size="large"
                        disabled={!availableModules.speaking}
                        onClick={async () => {
                          if (
                            !mockTest?.ieltsTest?.speakingId &&
                            !mockTest?.oetTest?.speakingId
                          ) {
                            alert(
                              "Speaking Test ID not found. Please try again."
                            );
                            return;
                          }

                          try {
                            clearPracticeTimer();
                            const createAttemptResponse =
                              await createTestAttempt({
                                mockTestId: testId,
                                isPracticeMode: false,
                                ieltsTest: {
                                  speakingId:
                                    mockTest.ieltsTest?.speakingId ||
                                    mockTest.oetTest?.speakingId,
                                },
                              }).unwrap();

                            console.log(
                              "Create attempt response:",
                              createAttemptResponse
                            );

                            // Extract student attempt data from nested response structure
                            const attemptData =
                              createAttemptResponse.data
                                ?.createStudentAttempt ||
                              createAttemptResponse.createStudentAttempt ||
                              createAttemptResponse;
                            const studentAttemptId =
                              attemptData._id || attemptData.studentAttemptId;

                            if (!studentAttemptId) {
                              console.error(
                                "No studentAttemptId found in response:",
                                createAttemptResponse
                              );
                              throw new Error(
                                "No studentAttemptId received from create attempt API"
                              );
                            }

                            // Extract speaking data from IELTS test response
                            const ieltsTest = attemptData.ieltsTest;
                            const speakingTestId = ieltsTest?.speakingId;
                            const speakingPartIds =
                              ieltsTest?.speakingPartIds || [];

                            if (!speakingTestId || !speakingPartIds.length) {
                              console.error("Speaking test data not found:", {
                                ieltsTest,
                                speakingTestId,
                                speakingPartIds,
                              });
                              throw new Error(
                                "Speaking test data not found in response"
                              );
                            }

                            console.log("Navigating to speaking with:", {
                              studentAttemptId,
                              speakingTestId,
                              speakingPartIds,
                            });

                            // Navigate to /speaking without audio method selection
                            navigate(`/speaking`, {
                              state: {
                                studentAttemptId,
                                speakingTestId,
                                speakingPartIds,
                                mockTest: {
                                  ...mockTest,
                                  ieltsTest: ieltsTest,
                                },
                                testId,
                                isPracticeMode: false,
                                practiceData: null,
                              },
                            });
                          } catch (error) {
                            console.error(
                              "Failed to start speaking test:",
                              error
                            );
                            alert(
                              "Failed to start speaking test. Please try again."
                            );
                          }
                        }}
                        sx={{
                          borderRadius: "16px",
                          background: availableModules.speaking
                            ? "#9E4646"
                            : "#ccc",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: { xs: "1.07rem", sm: "1.18rem" },
                          px: { xs: 5, sm: 8 },
                          py: { xs: 1.1, sm: 1.6 },
                          textTransform: "none",
                          boxShadow: "none",
                          "&:hover": {
                            background: availableModules.speaking
                              ? "#7a2323"
                              : "#ccc",
                          },
                          "&.Mui-disabled": {
                            background: "#ccc",
                            color: "#fff",
                          },
                        }}
                      >
                        Start
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            )}
            {/* PDF TAB */}
            {tab === 3 && (
              <div className="p-4 sm:p-6 flex flex-col items-center min-h-[300px] sm:min-h-[380px]">
                <div className="flex justify-center mb-4 sm:mb-6">
                  <div className="w-14 h-14 sm:w-18 sm:h-18 flex items-center justify-center bg-red-50 rounded-2xl">
                    <svg width="40" height="40" viewBox="0 0 48 48" fill="none">
                      <rect width="48" height="48" rx="12" fill="#f9ecec" />
                      <path
                        d="M18 14v-2a2 2 0 012-2h8a2 2 0 012 2v2"
                        stroke="#9D2235"
                        strokeWidth="2"
                      />
                      <rect
                        x="14"
                        y="14"
                        width="20"
                        height="20"
                        rx="2"
                        fill="#fff"
                        stroke="#9D2235"
                        strokeWidth="2"
                      />
                      <text
                        x="24"
                        y="32"
                        textAnchor="middle"
                        fill="#9D2235"
                        fontWeight="bold"
                        fontSize="12"
                        fontFamily="Arial"
                      >
                        PDF
                      </text>
                    </svg>
                  </div>
                </div>
                <h3 className="font-bold text-lg sm:text-xl mb-3 text-center">
                  IELTS Exam Test – PDFS
                </h3>
                <p className="text-gray-500 text-sm sm:text-base text-center max-w-xs sm:max-w-md mb-6 sm:mb-8 leading-relaxed">
                  Download IELTS Exam Tests PDF file – includes Listening,
                  Reading, Writing, and Speaking test
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 w-full max-w-none sm:max-w-2xl lg:max-w-3xl px-1 sm:px-0">
                  {[
                    {
                      label: "Listening Test",
                      url: pdfApiData?.data?.data?.mockTests?.listening?.pdfUrl,
                    },
                    {
                      label: "Reading Test",
                      url: pdfApiData?.data?.data?.mockTests?.reading?.pdfUrl,
                    },
                    {
                      label: "Writing Test",
                      url: pdfApiData?.data?.data?.mockTests?.writing?.pdfUrl,
                    },
                    {
                      label: "Speaking Test",
                      url: pdfApiData?.data?.data?.mockTests?.speaking?.pdfUrl,
                    },
                    {
                      label: "Listening Answers",
                      url: pdfApiData?.data?.data?.mockTests?.listening
                        ?.answersKeysPdfUrl,
                    },
                    {
                      label: "Reading Answers",
                      url: pdfApiData?.data?.data?.mockTests?.reading
                        ?.answersKeysPdfUrl,
                    },
                  ].map((pdf, idx) =>
                    !!pdf.url ? (
                      <div key={pdf.label} className="w-full">
                        <div className="border-2 border-gray-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center bg-white h-32 sm:h-40 lg:h-44 flex flex-col justify-between mx-auto transition-all duration-200 hover:shadow-lg hover:-translate-y-1 hover:border-red-800">
                          <div className="text-center mb-2">
                            <div className="flex justify-center mb-2">
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                className="text-red-800"
                              >
                                <path
                                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                                  fill="currentColor"
                                />
                                <polyline points="14,2 14,8 20,8" fill="#fff" />
                                <line
                                  x1="16"
                                  y1="13"
                                  x2="8"
                                  y2="13"
                                  stroke="#fff"
                                  strokeWidth="2"
                                />
                                <line
                                  x1="16"
                                  y1="17"
                                  x2="8"
                                  y2="17"
                                  stroke="#fff"
                                  strokeWidth="2"
                                />
                                <polyline
                                  points="10,9 9,9 8,9"
                                  stroke="#fff"
                                  strokeWidth="2"
                                />
                              </svg>
                            </div>
                            <h4 className="font-semibold text-xs sm:text-sm lg:text-base text-gray-800 text-center leading-tight px-1 line-clamp-2">
                              {pdf.label}
                            </h4>
                          </div>
                          <button
                            onClick={() => window.open(pdf.url, "_blank")}
                            className="w-full bg-red-800 hover:bg-red-900 text-white font-semibold text-xs sm:text-sm rounded-lg sm:rounded-xl px-2 sm:px-4 py-2 sm:py-2.5 transition-all duration-150 shadow-none hover:shadow-md hover:-translate-y-0.5"
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    ) : null
                  )}
                </div>
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleCancel}
                    className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-6 py-2 rounded-lg transition-colors duration-150"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Error/Validation Notification Snackbar */}
      <Snackbar
        open={errorNotification.open}
        autoHideDuration={4000}
        onClose={() =>
          setErrorNotification({ ...errorNotification, open: false })
        }
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            setErrorNotification({ ...errorNotification, open: false })
          }
          severity={errorNotification.severity}
          sx={{ width: "100%" }}
        >
          {errorNotification.message}
        </Alert>
      </Snackbar>
    </>
  );
}
